<template>
  <div class="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 auth-background">
    <div class="max-w-md w-full">
      <div class="glassmorphic-card">
        <div class="text-center mb-8">
          <router-link to="/" class="inline-block mb-4">
            <img
              src="/images/venmas-logo-1.png"
              alt="Venmas"
              class="h-16 mx-auto object-contain"
            />
          </router-link>
          <h2 class="text-3xl font-bold text-white mb-2">
            Reset your password
          </h2>
          <p class="text-white/70">
            Choose a new password for your Venmas account
          </p>
        </div>

        <form class="space-y-6" @submit.prevent="handleResetPassword">
          <div>
            <label for="password" class="block text-sm font-semibold text-white/90 mb-2">New Password</label>
            <input
              id="password"
              v-model="password"
              name="password"
              type="password"
              autocomplete="new-password"
              required
              class="glass-input"
              placeholder="Enter a strong password"
            />
            <ul class="mt-3 grid grid-cols-1 gap-1 text-xs text-white/70">
              <li
                v-for="requirement in passwordRequirements"
                :key="requirement.label"
                :class="requirement.met ? 'text-green-200' : 'text-white/65'"
              >
                {{ requirement.met ? 'OK' : '-' }} {{ requirement.label }}
              </li>
            </ul>
          </div>

          <div>
            <label for="confirm-password" class="block text-sm font-semibold text-white/90 mb-2">Confirm Password</label>
            <input
              id="confirm-password"
              v-model="confirmPassword"
              name="confirm-password"
              type="password"
              autocomplete="new-password"
              required
              class="glass-input"
              placeholder="Confirm your password"
            />
          </div>

          <div v-if="error" class="p-4 bg-red-500/20 border border-red-400/30 rounded-lg text-red-200 text-sm text-center">
            {{ error }}
          </div>

          <div v-if="success" class="p-4 bg-green-500/20 border border-green-400/30 rounded-lg text-green-200 text-sm text-center">
            {{ success }}
          </div>

          <div>
            <button
              type="submit"
              :disabled="authStore.loading || !token"
              class="w-full py-3 px-4 text-sm font-medium rounded-xl text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style="background: #2563eb;"
            >
              {{ authStore.loading ? 'Resetting...' : 'Reset password' }}
            </button>
          </div>

          <div class="text-center">
            <router-link
              to="/login"
              class="text-white/80 hover:text-white text-sm transition-colors"
            >
              Back to sign in
            </router-link>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const password = ref('')
const confirmPassword = ref('')
const error = ref('')
const success = ref('')

const token = computed(() => {
  const value = route.query.token
  return typeof value === 'string' ? value : ''
})

async function handleResetPassword() {
  error.value = ''
  success.value = ''

  if (!token.value) {
    error.value = 'This reset link is missing a token.'
    return
  }

  if (password.value !== confirmPassword.value) {
    error.value = 'Passwords do not match.'
    return
  }

  if (!isPasswordValid.value) {
    error.value = 'Use at least 8 characters with uppercase, lowercase, a number, and one symbol: @$!%*?&.'
    return
  }

  try {
    await authStore.resetPassword(token.value, password.value)
    success.value = 'Password reset successfully. Redirecting to sign in...'
    window.setTimeout(() => {
      router.push('/login')
    }, 1500)
  } catch (err: unknown) {
    const apiError = (err as { error?: unknown })?.error
    error.value =
      typeof apiError === 'string'
        ? apiError
        : 'Failed to reset password.'
  }
}

const passwordRequirements = computed(() => [
  { label: 'At least 8 characters', met: password.value.length >= 8 },
  { label: 'One uppercase letter', met: /[A-Z]/.test(password.value) },
  { label: 'One lowercase letter', met: /[a-z]/.test(password.value) },
  { label: 'One number', met: /\d/.test(password.value) },
  { label: 'One symbol: @$!%*?&', met: /[@$!%*?&]/.test(password.value) },
])

const isPasswordValid = computed(() => passwordRequirements.value.every((requirement) => requirement.met))
</script>

<style scoped>
.auth-background {
  background: #1e3a5f url('/images/auth-bg.png') center / cover no-repeat fixed;
  position: relative;
  isolation: isolate;
}

.auth-background::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 0;
}

.auth-background > * {
  position: relative;
  z-index: 1;
}

.glassmorphic-card {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  padding: 2rem;
}

.glass-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  outline: none;
}

.glass-input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.glass-input:focus {
  border-color: rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.2);
}
</style>
